import { Loader2, ArrowDown } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { cn } from "@/lib/utils";

type Props = {
  scrollTarget: HTMLElement | null;
  enabled?: boolean;
};

/**
 * Global pull-to-refresh indicator. Invalidates the entire React Query cache
 * so every list page refetches its data. Also dispatches the
 * `devices:cache-invalidate` window event used by older imperative code.
 */
export function PullToRefresh({ scrollTarget, enabled = true }: Props) {
  const qc = useQueryClient();
  const busyRef = useRef(false);

  const { pullDistance, refreshing } = usePullToRefresh({
    target: scrollTarget,
    enabled,
    onRefresh: async () => {
      if (busyRef.current) return;
      busyRef.current = true;
      try {
        window.dispatchEvent(new CustomEvent("devices:cache-invalidate"));
        await qc.invalidateQueries();
      } finally {
        busyRef.current = false;
      }
    },
  });

  const visible = pullDistance > 0 || refreshing;
  const progress = Math.min(1, pullDistance / 70);
  const translate = refreshing ? 56 : Math.min(72, pullDistance);

  return (
    <div
      aria-hidden={!visible}
      className={cn(
        "pointer-events-none fixed left-1/2 top-16 z-40 -translate-x-1/2",
        "flex h-10 w-10 items-center justify-center rounded-full",
        "bg-background/95 text-foreground shadow-md backdrop-blur",
        "transition-opacity",
        visible ? "opacity-100" : "opacity-0"
      )}
      style={{
        transform: `translate(-50%, ${translate - 40}px)`,
        transition: refreshing ? "transform 200ms ease-out" : undefined,
      }}
    >
      {refreshing ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <ArrowDown
          className="h-4 w-4 transition-transform"
          style={{ transform: `rotate(${progress * 180}deg)` }}
        />
      )}
    </div>
  );
}

export default PullToRefresh;
